// Pure transform — flatten nested milestones to legacy top-level fields.

// care_period.status — stored source of truth for the CMS lifecycle (eligibility →
// alignment → unalignment). Mirrors scripts/care-period-status-groups.mjs (the n8n
// canonical source). Spec: docs/specs/2026-06-24-cms-status-source-of-truth.md.
export type CarePeriodStatus =
  | 'pending' | 'pending_eligibility' | 'pending_alignment' | 'ineligible'
  | 'active' | 'pending_unalignment' | 'unaligned' | 'completed' | 'withdrawn';

// Non-terminal periods — for reads that resolve "the patient's current period".
export const NON_TERMINAL_STATUSES: CarePeriodStatus[] = [
  'active', 'pending_alignment', 'pending_eligibility', 'ineligible', 'pending_unalignment',
];
// Still-aligned (active care continues until CMS confirms unalignment).
export const ACTIVE_CARE_STATUSES: CarePeriodStatus[] = ['active', 'pending_unalignment'];
// Alignment is gated on eligibility — only these may be sent to CMS $align (active = idempotent retry).
export const ALIGNABLE_STATUSES: CarePeriodStatus[] = ['pending_alignment', 'active'];

export const CARE_PERIOD_STATUS_LABEL: Record<CarePeriodStatus, string> = {
  pending: 'Pending',
  pending_eligibility: 'Pending eligibility',
  pending_alignment: 'Pending alignment',
  ineligible: 'Ineligible',
  active: 'Active',
  pending_unalignment: 'Pending unalignment',
  unaligned: 'Unaligned',
  completed: 'Completed',
  withdrawn: 'Withdrawn',
};

export interface CarePeriodLike {
  baseline_due_date?: string | null;
  q1_due_date?: string | null;
  q2_due_date?: string | null;
  q3_due_date?: string | null;
  final_due_date?: string | null;
  baseline_status?: string | null;
  q1_status?: string | null;
  q2_status?: string | null;
  q3_status?: string | null;
  final_status?: string | null;
  milestones?: {
    baseline?: { due_date?: string | null; status?: string | null };
    q1?: { due_date?: string | null; status?: string | null };
    q2?: { due_date?: string | null; status?: string | null };
    q3?: { due_date?: string | null; status?: string | null };
    final?: { due_date?: string | null; status?: string | null };
  };
  [key: string]: unknown;
}

export function flattenMilestones<T extends CarePeriodLike | null | undefined>(cp: T): T {
  if (!cp || !cp.milestones) return cp;
  const m = cp.milestones;
  return {
    ...cp,
    baseline_due_date: m.baseline?.due_date ?? cp.baseline_due_date,
    q1_due_date: m.q1?.due_date ?? cp.q1_due_date,
    q2_due_date: m.q2?.due_date ?? cp.q2_due_date,
    q3_due_date: m.q3?.due_date ?? cp.q3_due_date,
    final_due_date: m.final?.due_date ?? cp.final_due_date,
    baseline_status: m.baseline?.status ?? cp.baseline_status,
    q1_status: m.q1?.status ?? cp.q1_status,
    q2_status: m.q2?.status ?? cp.q2_status,
    q3_status: m.q3?.status ?? cp.q3_status,
    final_status: m.final?.status ?? cp.final_status,
  } as T;
}
